import { KFTextInput } from '@kf/ic2fx';
import { Divider, Grid, InputAdornment, Link, Typography } from '@mui/material';
import { IInterviewers } from 'modules/hiring/models/candidatePipeline.model';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ActionWall from 'shared/components/ActionWall';
import InfoPaper from 'shared/components/infoPaper';
import { colors } from 'styles/colors';
import { emailValidation, interviewerEmailValidation } from 'utilities/formValidators';
import EmployeeChip from './EmployeeChip';
import InterviewModalHeader from './InterviewPanelModalHeader';

interface IInterviewersModalProps {
  id: string;
  reqId: string;
  open: boolean;
  interviewers: IInterviewers[];
  jobTitle: string;
  loginUserEmail?: string;
  handleClose: () => void;
  handleSuccess: (interviewers: IInterviewers[]) => void;
}

const InterviewersModal = ({
  id,
  reqId,
  open,
  interviewers,
  jobTitle,
  loginUserEmail,
  handleClose,
  handleSuccess,
}: IInterviewersModalProps) => {
  const { t: translate } = useTranslation();

  const [searchTerm, setSearchTerm] = useState('');

  const [interviewersList, setInterviewersList] = useState<IInterviewers[]>(interviewers);
  const [isError, setIsError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    if (interviewersList.length === 0) setDefaultInterviewer();
  }, []);

  const closeChipHandler = (interviewEmail: string) => {
    setInterviewersList((prev) => {
      return prev.filter((e) => e.interviewerEmail !== interviewEmail);
    });
  };
  const clearAllHandler = () => {
    setIsError(false);
    setSearchTerm('');
    // if (interviewers.length > 0) setInterviewersList(interviewers);
    // else setDefaultInterviewer();
  };

  const setDefaultInterviewer = () => {
    if (loginUserEmail) {
      setInterviewersList([
        {
          interviewerEmail: loginUserEmail,
          interviewerName: '',
          saved: false,
        },
      ]);
    }
  };
  const addInterviewerHandler = () => {
    if (interviewerEmailValidation(searchTerm)) {
      if (
        !interviewersList?.some(
          (el) => el.interviewerEmail?.toLowerCase() === searchTerm.toLowerCase(),
        )
      ) {
        setInterviewersList([
          ...interviewersList,
          { interviewerEmail: searchTerm, interviewerName: '', saved: false },
        ]);
        setSearchTerm('');
      } else {
        setErrorMessage(translate('youHaveEnteredDuplicateEmailAddress'));
        setIsError(true);
      }
    } else {
      setErrorMessage(translate('youHaveEnteredInvalidEmailAddress'));
      setIsError(true);
    }
  };

  const getIsSuccessDisabled = () => {
    return interviewersList.length === 0
      ? true
      : interviewersList.length < interviewers.length
        ? false
        : !interviewersList.some((e) => e.saved === false);
  };

  const remainingInterviewers = 6 - interviewersList.length;

  return (
    <ActionWall
      dialogId={id}
      open={open}
      closeIconButton={false}
      successDisabled={getIsSuccessDisabled()}
      titleClassName='kfic2-panelTitleBold'
      headerContent={
        <InterviewModalHeader
          reqId={reqId}
          title={translate('invitePeopleToInterviewForThisRequisition')}
          subTitle={jobTitle}
        />
      }
      id={`${id}-modal`}
      tabIndex={0}
      aria-label={translate('makeDecision')}
      centerAlignment={true}
      dialogPaperSxProps={{
        p: 2.5,
        m: 0,
        maxWidth: '53.25rem',
      }}
      dialogContentSxProps={{
        mb: 0,
        pb: 0,
        pt: '1.25rem !important',
        maxHeight: 'calc(100% - 9.75rem)',
        scrollbarWidth: 'thin',
        '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
          backgroundColor: colors?.tertiary.white,
          width: '1rem',
        },
        '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
          borderRadius: '0.6rem',
          backgroundColor: colors?.background.darkGray,
          border: `0.25rem ${colors?.tertiary.white} solid `,
        },
      }}
      dialogTitleSxProps={{
        px: 0,
        mb: 0,
        zIndex: 1,
        fontSize: '20px',
        fontFamily: 'Gotham',
        lineHeight: '28px',
        fontweight: 700,
      }}
      closeButtonStyleProps={{
        top: 0,
        padding: 0,
      }}
      closeIconStyleProps={{
        width: '1.25rem',
        height: '1.25rem',
      }}
      sxTitle={{
        mt: 0,
      }}
      saveBtnSxProps={{}}
      footerContent={
        <Grid container>
          <Divider
            id='interview-modal-footer-divider'
            data-testid='interview-modal-footer-divider'
            sx={{
              mt: ({ typography }) => typography.pxToRem(24),
              mb: ({ typography }) => typography.pxToRem(0),
              width: '100%',
            }}
          />
        </Grid>
      }
      onSuccess={() => {
        handleSuccess(interviewersList);
      }}
      onCancel={() => {
        handleClose();
      }}
      outlinedCancelButton
      dialogActionsSxProps={{ mt: 1, width: 'fit-content', pr: 0 }}
      dialogActionsPDivSxProps={{ display: 'flex' }}
      successLabel={translate('save')}
    >
      <Grid container data-testid='interview-modal-body-container'>
        <Grid item xs={12}>
          <Grid container>
            <Grid item xs={10}>
              <Typography
                id='interview-modal-body-label1'
                data-testid='interview-modal-body-label1'
                className='kfic2-bodyText'
              >
                 {interviewersList.length === 0
                 ? 'You can add 6 interviewers:'
                 : remainingInterviewers === 0
                 ? 'You can add 0 interviewers:'
                 : `You can add ${remainingInterviewers} more interviewer${remainingInterviewers === 1 ? '' : 's'}:`}
              </Typography>
            </Grid>
            <Grid item xs={2} sx={{ textAlign: 'end' }}>
              <Link
                component='button'
                className='kfic2-bodyTextSmallLink'
                data-testid='interview-modal-clear-all-button'
                aria-label={translate('clearAll') as string}
                tabIndex={0}
                onClick={clearAllHandler}
                onKeyUp={(e: React.KeyboardEvent<HTMLButtonElement>) => {
                  if (e.key === 'Enter') clearAllHandler();
                }}
              >
                {translate('clearAll')}
              </Link>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} sx={{ pt: 2 }}>
          <KFTextInput
            placeholder={translate('addInterviewersByEmail')}
            value={searchTerm}
            disabled={interviewersList.length >= 6}
            onChange={(e) => {
              if (e.target.value.length <= 100) setSearchTerm(e.target.value);
              setIsError(false);
            }}
            onKeyUp={(e) => {
              if (e.key === 'Enter' && searchTerm !== '') addInterviewerHandler();
            }}
            sx={{
              width: '100%',
              color: 'red !important',
            }}
            InputLabelProps={{
              sx: {
                pb: ({ typography }: any) => typography.pxToRem(10),
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position='end' sx={{ pl: 1 }}>
                  {searchTerm !== '' && (
                    <Link
                      component='button'
                      className='kfic2-bodyTextSmallLink'
                      data-testid='interview-modal-add-button'
                      aria-label={translate('add') as string}
                      tabIndex={0}
                      onClick={addInterviewerHandler}
                      onKeyUp={(e: React.KeyboardEvent<HTMLButtonElement>) => {
                        if (e.key === 'Enter') addInterviewerHandler();
                      }}
                    >
                      {translate('add')}
                    </Link>
                  )}
                </InputAdornment>
              ),
            }}
            id='interview-email-input'
            data-testid='interview-email-input'
            className='kfic2-bodyText'
          />
          {isError && (
            <Typography
              id='interview-modal-error-label'
              data-testid='interview-modal-error-label'
              className='kfic2-metadata'
              sx={{ color: `${colors.status.error} !important`, mt: 2 }}
            >
              {errorMessage}
            </Typography>
          )}
        </Grid>
        <Grid item xs={12} sx={{}}>
          {interviewersList.length >= 6 && (
            <Grid item xs={12} sx={{ pt: 2 }}>
              <InfoPaper>
                <>This panel has reached the maximum number of interviewers. To add another interviewer, please remove an existing one.</>
              </InfoPaper>
            </Grid>
          )}
          {interviewersList.length > 0 && (
            <Grid item xs={12} sx={{ pt: 2 }}>
              <Grid
                container
                sx={{
                  display: 'flex',
                  gap: '16px',
                  padding: ({ typography }) =>
                    `${typography.pxToRem(16)} ${typography.pxToRem(20)}`,
                  backgroundColor: '#F8F9FB',
                }}
              >
                {interviewersList?.map((e, index) => {
                  return (
                    <EmployeeChip
                      key={`emp-pill-${index}-${e.interviewerId}`}
                      index={index}
                      employeeFullName={e.interviewerEmail}
                      onClose={() => closeChipHandler(e.interviewerEmail)}
                    />
                  );
                })}
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>
    </ActionWall>
  );
};

export default InterviewersModal;
